export enum TooltipType {
  TEXT = 'text',
  LOOKUP = 'lookup',
  SPECIAL = 'special',
}

/**
 * The data attributes understood by the tooltip platform.
 *
 * Most boolean attributes are presence-based. Their value is therefore `true`
 * when present and `undefined` when absent. `data-tooltip-submenu-open` is the
 * exception: the tooltip platform reads its true/false value.
 */
export type TooltipDataAttributes = {
  'data-tooltip': string
  'data-tooltip-type': TooltipType
  'data-tooltip-key'?: string
  'data-tooltip-subtext'?: string
  'data-tooltip-show-immediately'?: true
  'data-tooltip-hide-immediately'?: true
  'data-tooltip-show-above'?: true
  'data-tooltip-show-below'?: true
  'data-tooltip-show-left'?: true
  'data-tooltip-show-right'?: true
  'data-tooltip-interactive'?: true
  'data-tooltip-light-mode'?: true
  'data-tooltip-ignore-mouse'?: true
  'data-tooltip-show-on-focus'?: true
  'data-tooltip-timeout-delay'?: number
  'data-tooltip-max-width'?: number
  'data-tooltip-max-lines'?: number
  'data-tooltip-shortcut-key'?: string
  'data-tooltip-shortcut'?: string
  'data-tooltip-bottom-flip-margin'?: number
  'data-tooltip-offset-x'?: number
  'data-tooltip-offset-y'?: number
  'data-tooltip-text-left'?: true
  'data-tooltip-text-right'?: true
  'data-tooltip-text-inline'?: true
  'data-tooltip-tip-align-left'?: true
  'data-tooltip-tip-align-right'?: true
  'data-tooltip-precise'?: true
  'data-tooltip-proxy-element-id'?: string
  'data-tooltip-show-on-target-only'?: true
  'data-tooltip-submenu-open'?: boolean
  'data-tooltip-editor-type'?: string
  'data-tooltip-url-string'?: string
  'data-tooltip-can-edit'?: boolean
  'data-tooltip-entity-key'?: string
  'data-tooltip-target-ref-key'?: string
  'data-tooltip-unconstrain-width'?: boolean
}

/** Options accepted by {@link getTooltipProps}. */
export type TooltipProps = {
  tooltip: string
  type: TooltipType
  tooltipKey?: string
  subtext?: string
  showImmediately?: boolean
  hideImmediately?: boolean
  showAbove?: boolean
  showBelow?: boolean
  showLeft?: boolean
  showRight?: boolean
  interactive?: boolean
  lightMode?: boolean
  ignoreMouse?: boolean
  showOnFocus?: boolean
  timeoutDelay?: number
  maxWidth?: number
  maxLines?: number
  shortcutKey?: string
  shortcut?: string
  bottomFlipMargin?: number
  offsetX?: number
  offsetY?: number
  textLeft?: boolean
  textRight?: boolean
  textInline?: boolean
  tipAlignLeft?: boolean
  tipAlignRight?: boolean
  precise?: boolean
  proxyElementId?: string
  showOnTargetOnly?: boolean
  submenuOpen?: boolean
  editorType?: string
  urlString?: string
  canEdit?: boolean
  entityKey?: string
  targetRefKey?: string
  unconstrainWidth?: boolean
}

type Falsy = false | '' | 0 | null | undefined

/**
 * Returns strongly typed attributes for spreading onto a tooltip target.
 *
 * @example
 * <button
 *   {...getTooltipProps({ tooltip: 'Copy', type: TooltipType.TEXT, showAbove: true })}
 * />
 */
export function getTooltipProps(props: TooltipProps): TooltipDataAttributes
export function getTooltipProps(props: TooltipProps | Falsy): Partial<TooltipDataAttributes>
export function getTooltipProps(props: TooltipProps | Falsy): Partial<TooltipDataAttributes> {
  if (!props) return {}

  const {
    tooltip,
    type,
    tooltipKey,
    subtext,
    showImmediately,
    hideImmediately,
    showAbove,
    showBelow,
    showLeft,
    showRight,
    interactive,
    lightMode,
    ignoreMouse,
    showOnFocus,
    timeoutDelay,
    maxWidth,
    maxLines,
    shortcutKey,
    shortcut,
    bottomFlipMargin,
    offsetX,
    offsetY,
    textLeft,
    textRight,
    textInline,
    tipAlignLeft,
    tipAlignRight,
    precise,
    proxyElementId,
    showOnTargetOnly,
    submenuOpen,
    editorType,
    urlString,
    canEdit,
    entityKey,
    targetRefKey,
    unconstrainWidth,
  } = props
  const attributes: TooltipDataAttributes = {
    'data-tooltip': tooltip,
    'data-tooltip-type': type,
  }

  if (tooltipKey !== undefined) attributes['data-tooltip-key'] = tooltipKey
  if (subtext !== undefined) attributes['data-tooltip-subtext'] = subtext
  if (showImmediately) attributes['data-tooltip-show-immediately'] = true
  if (hideImmediately) attributes['data-tooltip-hide-immediately'] = true
  if (showAbove) attributes['data-tooltip-show-above'] = true
  if (showBelow) attributes['data-tooltip-show-below'] = true
  if (showLeft) attributes['data-tooltip-show-left'] = true
  if (showRight) attributes['data-tooltip-show-right'] = true
  if (interactive) attributes['data-tooltip-interactive'] = true
  if (lightMode) attributes['data-tooltip-light-mode'] = true
  if (ignoreMouse) attributes['data-tooltip-ignore-mouse'] = true
  if (showOnFocus) attributes['data-tooltip-show-on-focus'] = true
  if (timeoutDelay !== undefined) attributes['data-tooltip-timeout-delay'] = timeoutDelay
  if (maxWidth !== undefined) attributes['data-tooltip-max-width'] = maxWidth
  if (maxLines !== undefined) attributes['data-tooltip-max-lines'] = maxLines
  if (shortcutKey !== undefined) attributes['data-tooltip-shortcut-key'] = shortcutKey
  if (shortcut !== undefined) attributes['data-tooltip-shortcut'] = shortcut
  if (bottomFlipMargin !== undefined) {
    attributes['data-tooltip-bottom-flip-margin'] = bottomFlipMargin
  }
  if (offsetX !== undefined) attributes['data-tooltip-offset-x'] = offsetX
  if (offsetY !== undefined) attributes['data-tooltip-offset-y'] = offsetY
  if (textLeft) attributes['data-tooltip-text-left'] = true
  if (textRight) attributes['data-tooltip-text-right'] = true
  if (textInline) attributes['data-tooltip-text-inline'] = true
  if (tipAlignLeft) attributes['data-tooltip-tip-align-left'] = true
  if (tipAlignRight) attributes['data-tooltip-tip-align-right'] = true
  if (precise) attributes['data-tooltip-precise'] = true
  if (proxyElementId !== undefined) {
    attributes['data-tooltip-proxy-element-id'] = proxyElementId
  }
  if (showOnTargetOnly) attributes['data-tooltip-show-on-target-only'] = true
  if (submenuOpen !== undefined) attributes['data-tooltip-submenu-open'] = submenuOpen
  if (editorType !== undefined) attributes['data-tooltip-editor-type'] = editorType
  if (urlString !== undefined) attributes['data-tooltip-url-string'] = urlString
  if (canEdit !== undefined) attributes['data-tooltip-can-edit'] = canEdit
  if (entityKey !== undefined) attributes['data-tooltip-entity-key'] = entityKey
  if (targetRefKey !== undefined) attributes['data-tooltip-target-ref-key'] = targetRefKey
  if (unconstrainWidth !== undefined) {
    attributes['data-tooltip-unconstrain-width'] = unconstrainWidth
  }

  return attributes
}
